/**
 * Page top for pages inside the app shell (Library, Templates, …) — a white
 * band across the page, like the Courses page:
 *
 *   Title                              [ 🔍 Search … ]  [ + Create … ]
 *
 * ONE line, always (29 Sep 2026): the title sits at the same place and height
 * on every page, the Courses page included. A back arrow, where a page has one,
 * hangs in the left margin on desktop so it never pushes the title along.
 * Rendered OUTSIDE the page's own centred container so the band can reach
 * both edges; --shell-px (set by AppShell) undoes the shell's side padding.
 * Plain back arrow, no box (Jasmine, 29 Sep).
 */

import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { T } from './orgTheme'
import { isPlainClick, spaLink } from '@/lib/spaLink'

export function PageHeader({ backTo, backLabel, title, badges, action, search, below }: {
  backTo?: string
  /** Inner pages (5 Oct 2026): a small "‹ Attributes" link ABOVE the title, instead of the margin arrow. */
  backLabel?: string
  title: string
  /** Chips beside the title, e.g. the attribute's type and Standard / Custom. */
  badges?: ReactNode
  action?: ReactNode
  search?: { value: string; onChange: (v: string) => void; placeholder: string }
  /** A row under the title, inside the band (e.g. tabs). */
  below?: ReactNode
}) {
  const navigate = useNavigate()
  const loc = useLocation()
  const here = loc.pathname + loc.search
  return (
    <div className="sticky top-0 z-[9]"
      style={{ background: '#fff', borderBottom: `1px solid ${T.border}`, margin: '0 calc(var(--shell-px, 0px) * -1)', padding: '0 var(--shell-px, 0px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: backTo && backLabel ? '14px 24px 20px' : '24px 24px 20px', minHeight: 42, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        {backTo && backLabel && (
          <a {...spaLink(backTo, () => navigate(backTo))} className="hover:text-[#5B21B6]"
            style={{ flex: '0 0 100%', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14.5, color: T.muted, textDecoration: 'none', marginBottom: -6 }}>
            <svg width="12" height="12" viewBox="0 0 16 16"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {backLabel}
          </a>
        )}
        {backTo && !backLabel && (
          <a {...spaLink(backTo, () => navigate(backTo))} aria-label="Back" className="hover:bg-[#F4F2F6] md:-ml-[46px]"
            style={{ width: 34, height: 34, flex: 'none', display: 'grid', placeItems: 'center', border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer', color: T.ink, textDecoration: 'none' }}>
            <svg width="16" height="16" viewBox="0 0 16 16"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </a>
        )}
        <h1 style={{ flex: badges ? '0 1 auto' : '1 1 auto', minWidth: 0, margin: 0, fontSize: 26, fontWeight: 600, letterSpacing: '-.01em', color: T.dark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {/* The heading links to this page itself: right-click → Open in new tab / Copy link,
              or Cmd/Ctrl-click. A plain click stays put (you're already here). */}
          <a href={here} onClick={e => { if (isPlainClick(e)) e.preventDefault() }} style={{ color: 'inherit', textDecoration: 'none' }}>{title}</a>
        </h1>
        {badges && <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{badges}</div>}
        {search && (
          <div style={{ position: 'relative', flex: '0 1 380px', minWidth: 200 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: T.faint }}>
              <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" fill="none" /><path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input value={search.value} onChange={e => search.onChange(e.target.value)} placeholder={search.placeholder} aria-label={search.placeholder}
              className="focus:border-[#7A3BD9] focus:shadow-[0_0_0_3px_#EFE6FC]"
              style={{ width: '100%', height: 42, padding: '0 12px 0 38px', border: `1px solid ${T.inputBorder}`, borderRadius: 10, background: '#fff', fontSize: 14.5, outline: 'none' }} />
          </div>
        )}
        {action && <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>{action}</div>}
      </div>
      {below && <div style={{ maxWidth: 1280, margin: '-8px auto 0', padding: '0 24px' }}>{below}</div>}
    </div>
  )
}

/**
 * The primary "+ Action" button used in PageHeader. With `href` it is a real
 * link (Cmd/Ctrl/middle-click opens it in a new tab) — for actions that open a page.
 */
/** A header button. `secondary`: outlined, for the second action beside a primary one.
 *  `disabled` (with `hint` as its tooltip) when the action isn't available right now. */
export function HeaderAction({ label, onClick, href, secondary, disabled, hint }: {
  label: string; onClick: () => void; href?: string; secondary?: boolean; disabled?: boolean; hint?: string
}) {
  const style = { height: 42, padding: '0 18px', borderRadius: 10, fontSize: 14.5, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' as const,
    display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', opacity: disabled ? 0.5 : 1,
    ...(secondary ? { border: `1px solid ${T.inputBorder}`, background: '#fff', color: T.dark } : { border: 'none', background: T.purple, color: '#fff' }) }
  const body = <><span style={{ fontSize: 18, fontWeight: 400, lineHeight: 1 }}>+</span> {label}</>
  const hover = disabled ? undefined : secondary ? 'hover:bg-[#FAF9FB]' : 'hover:bg-[#6A2FC4]'
  if (disabled) return <span title={hint}><button disabled style={style}>{body}</button></span>
  return href
    ? <a {...spaLink(href, onClick)} className={hover} style={style}>{body}</a>
    : <button onClick={onClick} className={hover} style={style}>{body}</button>
}
